import React, { useCallback, useState } from "react";
import moment from "moment";
import { Modal } from "react-bootstrap";

import { Button, Table } from "components";
import { useGetMerchantsChangesMutation } from "queries";
import { ColumnTypes, ListResponseDto } from "types";

import * as table from "./tableColumns";

import styles from "./residualsModal.module.scss";

type ResidualsDataChangesModalProps = {
	show: boolean;
	onHide: () => void;
	data: ListResponseDto<any>;
	paidMonth: string;
	paidYear: string;
	handleRerunData: Function;
};

const ResidualsDataChangesModal = ({
	show,
	onHide,
	data,
	paidMonth,
	paidYear,
	handleRerunData,
}: ResidualsDataChangesModalProps) => {
	const [page, setPage] = useState<number>(0);
	const [changes, setChanges] = useState<any>(data);

	const {
		mutateAsync: getMerchantsChanges,
		isLoading: isLoadingMerchantsChanges,
	} = useGetMerchantsChangesMutation({
		paidMonth,
		paidYear,
	});

	const columns: ColumnTypes = [
		table.midColumn(),
		table.changesColumn(isLoadingMerchantsChanges),
	];

	const handlePageChange = useCallback(
		async ({ pageIndex: newIndex }) => {
			if (newIndex !== page) {
				setPage?.(newIndex);
				const merchantsChanges = await getMerchantsChanges({
					page: newIndex,
				});
				setChanges(merchantsChanges);
			}
		},
		[getMerchantsChanges, page],
	);

	const handleRerunNewData = () => {
		handleRerunData({ acceptMerchantChanges: true });
		onHide();
	};

	const handleRerunBackupData = () => {
		handleRerunData({ acceptMerchantChanges: false });
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton className="pb-0">
				<Modal.Title as="h6" className="text-center">
					Merchants residuals data has changed since{" "}
					{moment(paidMonth).format("MMMM")} {paidYear}. <br /> Do you
					want to proceed with new data?
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.residualsDataChangesBody}>
				<Table
					columns={columns}
					data={changes?.items}
					meta={{
						loading: isLoadingMerchantsChanges,
						totalItems: changes?.count,
					}}
					initialState={{}}
					handlePageChange={handlePageChange}
					bordered="full"
					transparent
					pageSize={5}
					stickyFooter
					stickyHeader
				/>
			</Modal.Body>
			<Modal.Footer className="justify-content-center pb-4">
				<Button
					variant="outline-primary"
					value="Cancel"
					onClick={onHide}
				/>
				<Button
					value="Run with backup data"
					className="mx-4"
					onClick={handleRerunBackupData}
				/>
				<Button
					value="Run with new data"
					onClick={handleRerunNewData}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default ResidualsDataChangesModal;
